// /scada/station/:id — station card (R2). Tabs: overview, single-line diagram,
// inverters/strings, КТП, BESS. Equipment detail is mock, consistent with the
// fleet overview numbers.
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard, StatusBadge, MetricTile, PowerGauge } from '@/components/scada/kit';
import CircularGauge from '@/components/base/CircularGauge';
import BessHistory from '@/components/scada/BessHistory';
import { Sld, InverterMatrix, KtpPanel } from '@/components/scada/stationViews';
import InverterPanel from '@/components/scada/InverterPanel';
import { InverterStrip, StationDayChart } from '@/components/scada/InverterStrip';
import BessConsolidated from '@/components/scada/BessConsolidated';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useStationTelemetry } from '@/scada/useTelemetry';
import { buildStationDetail } from '@/scada/mock/stationDetail';
import CommercialMetersWidget from '@/components/scada/CommercialMetersWidget';

type Tab = 'overview' | 'sld' | 'inverters' | 'ktp' | 'bess';

export default function ScadaStationPage() {
  const { id } = useParams();
  const cfg = STATIONS.find((s) => s.id === id);
  const { data: t } = useStationTelemetry(id ?? '');
  const [tab, setTab] = useState<Tab>('overview');
  const detail = useMemo(() => (cfg && t ? buildStationDetail(cfg, t) : null), [cfg, t]);

  if (!cfg || !t || !detail) {
    return <ScadaLayout active="dispatch"><SectionCard title="Станція не знайдена"><span style={{ color: scada.textMuted }}>Невідомий ідентифікатор станції.</span></SectionCard></ScadaLayout>;
  }

  const gaugeColor = t.state === 'alarm' || t.state === 'offline' ? scada.state.alarm
    : t.limitReason ? scada.state.curtailed : scada.state.ok;

  const tabs: { key: Tab; label: string; icon: string; show: boolean }[] = [
    { key: 'overview', label: 'Огляд', icon: 'ri-dashboard-line', show: true },
    { key: 'sld', label: 'Схема', icon: 'ri-node-tree', show: true },
    { key: 'inverters', label: 'Інвертори', icon: 'ri-cpu-line', show: true },
    { key: 'ktp', label: 'КТП', icon: 'ri-archive-2-line', show: true },
    { key: 'bess', label: 'УЗЕ', icon: 'ri-battery-2-charge-line', show: cfg.hasBess || !!t.bess },
  ];

  return (
    <ScadaLayout active="dispatch">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-3">
          <Link to="/scada/dispatch" className="inline-flex items-center gap-1.5 rounded-lg transition-colors" style={{ color: scada.textDim, fontSize: 13, textDecoration: 'none', background: scada.panel, border: `1px solid ${scada.border}`, padding: '5px 11px' }} onMouseEnter={(e) => { e.currentTarget.style.background = scada.panelHover; e.currentTarget.style.color = scada.text; }} onMouseLeave={(e) => { e.currentTarget.style.background = scada.panel; e.currentTarget.style.color = scada.textDim; }}><i className="ri-arrow-left-line" /> Усі станції</Link>
          <span style={{ color: scada.text, fontSize: 18, fontWeight: 700 }}>{cfg.name}</span>
          <StatusBadge state={t.state} />
        </div>
        <div style={{ color: scada.textMuted, fontSize: 12 }}>
          {cfg.legalEntity} · {cfg.region}{cfg.eicW ? ` · ${cfg.eicW}` : ''}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 16 }}>
        {tabs.filter((x) => x.show).map((x) => {
          const on = tab === x.key;
          return (
            <button key={x.key} onClick={() => setTab(x.key)}
              className="inline-flex items-center gap-1.5"
              style={{ background: on ? `${scada.accent}1A` : scada.panel2, color: on ? scada.text : scada.textDim, border: `1px solid ${on ? scada.accent + '60' : scada.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
              <i className={x.icon} style={{ color: on ? scada.accent : scada.textMuted }} />{x.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            <SectionCard fill title="Активна потужність" subtitle={`з ${(cfg.acCapacityKw / 1000).toFixed(2)} МВт встановленої`}>
              <div className="flex flex-col items-center" style={{ padding: 8 }}>
                <PowerGauge valueKw={t.activePowerKw} maxKw={cfg.acCapacityKw} color={gaugeColor} size={196}
                  label={`${cfg.acCapacityKw ? Math.round((t.activePowerKw / cfg.acCapacityKw) * 100) : 0}% завантаження`} />
                <div className="grid grid-cols-3 gap-2" style={{ marginTop: 16, width: '100%' }}>
                  {[
                    { l: 'Інвертори онлайн', v: `${t.invertersWorking}/${t.inverterCount}`, c: t.invertersWorking < t.inverterCount ? scada.state.warning : scada.state.ok },
                    { l: 'Генерація сьогодні', v: `${(t.dailyEnergyKwh / 1000).toFixed(1)}`, u: 'МВт·год', c: scada.text },
                    { l: 'Доступна', v: `${(t.availablePowerKw / 1000).toFixed(2)}`, u: 'МВт', c: scada.text },
                  ].map((s) => (
                    <div key={s.l} style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 8, padding: '8px 9px' }}>
                      <div style={{ color: scada.textMuted, fontSize: 10, lineHeight: 1.2 }}>{s.l}</div>
                      <div style={{ color: s.c, fontSize: 16, fontWeight: 700, marginTop: 2 }}>{s.v}{s.u && <span style={{ fontSize: 9, color: scada.textDim }}> {s.u}</span>}</div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
            {/* 2. Комерційний облік (DLMS) — без BESS займає 2/3 ширини (без збільшення висоти) */}
            <div className={`flex flex-col ${t.bess ? '' : 'md:col-span-2'}`}>
              {['bolgrad', 'prometheus', 'tabaki', 'nikopol'].includes(cfg.id) ? (
                <CommercialMetersWidget endpoint={`/scada/${cfg.id}/meters`} cols={t.bess ? 3 : 4} />
              ) : (
                <SectionCard fill title="Комерційний облік (DLMS)" subtitle="пряме DLMS-зчитування (ОЗД)">
                  <div className="flex items-center justify-center" style={{ flex: 1, minHeight: 130 }}>
                    <span style={{ color: scada.textMuted, fontSize: 13, textAlign: 'center' }}>Дані комерційного обліку недоступні для цієї станції</span>
                  </div>
                </SectionCard>
              )}
            </div>

            {/* 3. УЗЕ / Накопичувач — лише за наявності BESS (інакше колонка не займає місце) */}
            {t.bess && (
              <div className="flex flex-col">
                <BessConsolidated fill bess={t.bess} capacityKwh={cfg.bessCapacityKwh} />
              </div>
            )}
          </div>

          {/* Row 2: технічний стан інверторів · Row 3: рівномірність (для всіх станцій) */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <InverterStrip assetId={t.assetId} />
            <StationDayChart assetId={t.assetId} />
          </div>
        </>
      )}

      {/* SLD */}
      {tab === 'sld' && (
        <SectionCard title="Однолінійна схема станції" subtitle="Точка приєднання → КТП → інверторні групи">
          <Sld cfg={cfg} t={t} detail={detail} />
        </SectionCard>
      )}

      {/* INVERTERS */}
      {tab === 'inverters' && (
        t.assetId
          ? <InverterPanel assetId={t.assetId} />
          : (
            <SectionCard title={`Інвертори (${t.invertersWorking}/${t.inverterCount} в роботі)`} subtitle="Натисніть інвертор, щоб переглянути стрінги">
              <InverterMatrix detail={detail} />
            </SectionCard>
          )
      )}

      {/* КТП */}
      {tab === 'ktp' && (
        <SectionCard title="Трансформаторні підстанції (КТП)">
          <KtpPanel detail={detail} />
        </SectionCard>
      )}

      {/* BESS / УЗЕ */}
      {tab === 'bess' && t.bess && (() => {
        const b = t.bess;
        const modeLabel = b.mode === 'charging' ? 'Заряд' : b.mode === 'discharging' ? 'Розряд' : b.mode === 'fault' ? 'Аварія' : 'Очікує';
        const modeColor = b.mode === 'charging' ? scada.state.curtailed : b.mode === 'discharging' ? scada.state.ok : b.mode === 'fault' ? scada.state.alarm : scada.textDim;
        const socColor = b.socPct >= 80 ? scada.state.ok : b.socPct >= 30 ? scada.state.curtailed : scada.state.warning;
        const n = (v?: number | null, d = 1) => (v == null ? '—' : Number(v).toFixed(d));
        return (
          <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SOC gauge + headline */}
            <SectionCard title="Рівень заряду (SOC)">
              <div className="flex flex-col items-center" style={{ padding: 8 }}>
                <CircularGauge value={b.socPct} max={100} size={150} strokeWidth={12} color={socColor} trackColor={scada.panel2}>
                  <span style={{ color: scada.text, fontSize: 30, fontWeight: 700 }}>{n(b.socPct, 1)}</span>
                  <span style={{ color: scada.textDim, fontSize: 13 }}>%</span>
                </CircularGauge>
                <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                  <span style={{ color: modeColor, fontSize: 14, fontWeight: 600 }}>{modeLabel}</span>
                  <span style={{ color: scada.textMuted, fontSize: 12 }}>· {Math.abs(b.powerKw).toFixed(1)} кВт</span>
                </div>
                <span style={{ color: scada.textMuted, fontSize: 11, marginTop: 2 }}>SOH (стан здоровʼя): {n(b.sohPct, 0)}%</span>
              </div>
            </SectionCard>

            {/* Electrical + thermal */}
            <div className="md:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <MetricTile label="Потужність AC" value={n(b.powerKw, 1)} unit="кВт" accent={modeColor} quality={b.qualityFlag === 'ok' ? 'good' : 'stale'} />
                <MetricTile label="Потужність DC" value={n(b.dcPowerKw, 1)} unit="кВт" />
                <MetricTile label="Напруга батареї" value={n(b.batteryV, 1)} unit="В" />
                <MetricTile label="Струм батареї" value={n(b.batteryA, 1)} unit="А" />
                <MetricTile label="Температура" value={n(b.innerTempC, 0)} unit="°C" />
                <MetricTile label="Частота" value={n(b.gridHz, 2)} unit="Гц" />
                <MetricTile label="Заряд за добу" value={n(b.dailyChargeKwh, 1)} unit="кВт·год" />
                <MetricTile label="Розряд за добу" value={n(b.dailyDischargeKwh, 1)} unit="кВт·год" />
                <MetricTile label="Режим керування" value={t.controlMode === 'remote' ? 'Дистанц.' : t.controlMode} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" style={{ marginTop: 12 }}>
                <MetricTile label="Доступно зарядити" value={n(b.chargeAvailableKwh, 1)} unit="кВт·год" accent={scada.state.curtailed} />
                <MetricTile label="Доступно розрядити" value={n(b.dischargeAvailableKwh, 1)} unit="кВт·год" accent={scada.state.ok} />
                <MetricTile label="Дозвіл заряд / розряд" value={`${b.chargeAvailable ? '✓' : '✗'} / ${b.dischargeAvailable ? '✓' : '✗'}`} />
              </div>

              <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 12 }}>
                <span style={{ color: scada.textMuted, fontSize: 12 }}>Стан PCS: <span style={{ color: scada.textDim }}>{b.bmsStatus === 'no_charging' ? 'без заряду' : (b.bmsStatus ?? '—')}</span></span>
                {cfg.bessCapacityKwh ? <span style={{ color: scada.textMuted, fontSize: 12 }}>Ємність: <span style={{ color: scada.textDim }}>{cfg.bessCapacityKwh / 1000} МВт·год</span></span> : null}
              </div>
            </div>
          </div>
          <BessHistory assetId={t.assetId} />
          </>
        );
      })()}
    </ScadaLayout>
  );
}
